import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { 
  Package, 
  ArrowLeft, 
  Loader2, 
  RefreshCw,
  Building2,
  Search,
  X,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { supplierReportApiService, SupplierBalanceReportDto } from '../services/supplierReportApi';
import { useBranch } from '../contexts/BranchContext';
import { getPaginationConfig } from '../config/uiConfig';

const SupplierBalanceReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<SupplierBalanceReportDto[]>([]);
  
  // Filter and search state - initialize from URL params if present
  const [searchTerm, setSearchTerm] = useState(searchParams.get('supplier') || '');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const paginationConfig = getPaginationConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);

  const loadReport = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch to view the report');
      return;
    }

    try {
      setLoading(true);
      const response = await supplierReportApiService.getSupplierBalanceReportByBranch(selectedBranch);
      
      if (response.success && response.data) {
        setReportData(response.data);
        toast.success('Report loaded successfully');
      } else {
        toast.error(response.message || 'Failed to load report');
        setReportData([]);
      }
    } catch (error) {
      // console.error('Error loading report:', error);
      toast.error('Failed to load report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      loadReport();
    }
  }, [selectedBranch]);

  // Get unique categories from report data
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    reportData.forEach((item) => {
      if (item.rawItemCategoryName) {
        categories.add(item.rawItemCategoryName);
      }
    });
    return Array.from(categories).sort();
  }, [reportData]);

  // Filter data based on search term, category, and remaining quantity
  const filteredData = useMemo(() => {
    let data = reportData.filter((item) => {
      // Filter: only show items where po_qty - receive_qty > 0
      const remainingQty = (item.poQty || 0) - (item.receiveQty || 0);
      if (remainingQty <= 0) {
        return false;
      }
      
      // Category filter
      if (selectedCategory && item.rawItemCategoryName !== selectedCategory) {
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.poNumber?.toLowerCase().includes(searchLower) ||
          item.rawItemName?.toLowerCase().includes(searchLower) ||
          item.rawItemCategoryName?.toLowerCase().includes(searchLower) ||
          item.sku?.toLowerCase().includes(searchLower) ||
          item.supplierName?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
    
    // Apply sorting
    if (sortColumn) {
      data = [...data].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortColumn) {
          case 'poNumber':
            aValue = a.poNumber || '';
            bValue = b.poNumber || '';
            break;
          case 'rawItemName':
            aValue = a.rawItemName || '';
            bValue = b.rawItemName || '';
            break;
          case 'category':
            aValue = a.rawItemCategoryName || '';
            bValue = b.rawItemCategoryName || '';
            break;
          case 'sku':
            aValue = a.sku || '';
            bValue = b.sku || '';
            break;
          case 'supplierName':
            aValue = a.supplierName || '';
            bValue = b.supplierName || '';
            break;
          case 'poQty':
            aValue = a.poQty || 0;
            bValue = b.poQty || 0;
            break;
          case 'receiveQty':
            aValue = a.receiveQty || 0;
            bValue = b.receiveQty || 0;
            break;
          case 'remainingQty':
            aValue = (a.poQty || 0) - (a.receiveQty || 0);
            bValue = (b.poQty || 0) - (b.receiveQty || 0);
            break;
          default:
            return 0;
        }
        
        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Handle number comparison
        return sortDirection === 'asc' 
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      });
    }
    
    return data;
  }, [reportData, searchTerm, selectedCategory, sortColumn, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Reset to first page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Define CSV headers
      const headers = [
        'PO Number',
        'Raw Item Name',
        'Category',
        'SKU',
        'Supplier Name',
        'PO Qty',
        'Receive Qty',
        'Remaining Qty'
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(','),
        ...filteredData.map((item) => {
          const remainingQty = (item.poQty || 0) - (item.receiveQty || 0);
          const row = [
            `"${item.poNumber || ''}"`,
            `"${(item.rawItemName || '').replace(/"/g, '""')}"`,
            `"${(item.rawItemCategoryName || '').replace(/"/g, '""')}"`,
            `"${item.sku || ''}"`,
            `"${(item.supplierName || '').replace(/"/g, '""')}"`,
            item.poQty?.toFixed(2) || '0.00',
            item.receiveQty?.toFixed(2) || '0.00',
            remainingQty.toFixed(2) || '0.00'
          ];
          return row.join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const branchSuffix = selectedBranch ? `_Branch-${selectedBranch}` : '';
      const filename = `Supplier_Balance_Report${branchSuffix}_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${filteredData.length} records to CSV`);
    } catch (error) {
      // console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Supplier Balance Report</CardTitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadReport}
                  disabled={loading || !selectedBranch}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={filteredData.length === 0}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Report Content */}
        {!selectedBranch ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">Please select a branch to view the report</p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 mr-3" />
            <span className="text-gray-600">Loading report data...</span>
          </div>
        ) : reportData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">No data available for this report</p>
              <p className="text-sm text-gray-500 mt-2">
                Try selecting a different branch or check back later
              </p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">No records match your filters</p>
              <p className="text-sm text-gray-500 mt-2">
                Try adjusting your search or category filter
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filters and Search */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by PO number, item name, category, SKU, or supplier..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Category Filter */}
              <div className="w-full sm:w-64">
                <Select 
                  value={selectedCategory ? selectedCategory : "all"} 
                  onValueChange={(value) => setSelectedCategory(value === "all" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search category...">
                    <SelectItem value="all">All Categories</SelectItem>
                    {availableCategories.length === 0 ? (
                      <SelectItem value="no-categories" disabled>No categories found</SelectItem>
                    ) : (
                      availableCategories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))
                    )}
                  </SelectContentWithSearch>
                </Select>
              </div>
            </div>

            {/* Table Controls */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {filteredData.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} {filteredData.length === 1 ? 'record' : 'records'}
                {reportData.length !== filteredData.length && (
                  <span className="text-gray-400 ml-1">
                    (filtered from {reportData.length} total)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Items per page:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paginationConfig.itemsPerPageOptions.map((option) => (
                      <SelectItem key={option} value={option.toString()}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Main Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('poNumber')}
                      >
                        <div className="flex items-center gap-2">
                          PO Number
                          {getSortIcon('poNumber')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold w-48 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('rawItemName')}
                      >
                        <div className="flex items-center gap-2">
                          Raw Item Name
                          {getSortIcon('rawItemName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('category')}
                      >
                        <div className="flex items-center gap-2">
                          Category
                          {getSortIcon('category')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('sku')}
                      >
                        <div className="flex items-center gap-2">
                          SKU
                          {getSortIcon('sku')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('supplierName')}
                      >
                        <div className="flex items-center gap-2">
                          Supplier Name
                          {getSortIcon('supplierName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('poQty')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          PO Qty
                          {getSortIcon('poQty')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('receiveQty')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Receive Qty
                          {getSortIcon('receiveQty')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('remainingQty')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Remaining Qty
                          {getSortIcon('remainingQty')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((item, index) => {
                      const remainingQty = (item.poQty || 0) - (item.receiveQty || 0);
                      return (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            {item.poNumber || 'N/A'}
                          </TableCell>
                          <TableCell className="w-48 max-w-48">
                            <div className="break-words whitespace-normal">
                              {item.rawItemName || 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.rawItemCategoryName || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {item.sku || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {item.supplierName || 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(item.poQty)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(item.receiveQty)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={remainingQty > 0 ? 'font-semibold text-primary-600' : ''}>
                              {formatNumber(remainingQty)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierBalanceReportPage;

